import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Activity, 
  Droplets, 
  Thermometer, 
  MapPin, 
  Clock, 
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Beaker
} from 'lucide-react';

const SpinTouchPage = () => {
  const { user, token } = useAuth();
  const [latestAnalysis, setLatestAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);

  const fetchLatestAnalysis = async () => {
    try {
      const response = await fetch('/api/spintouch/latest', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLatestAnalysis(data);
      }
    } catch (error) {
      console.error('Error fetching latest analysis:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch('/api/spintouch/history', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const simulateSpinTouchData = async () => {
    setSimulating(true);
    try {
      const response = await fetch('/api/spintouch/simulate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Simulation result:', data);
        // Refresh data
        await fetchLatestAnalysis();
        await fetchHistory();
      }
    } catch (error) {
      console.error('Error simulating data:', error);
    } finally {
      setSimulating(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLatestAnalysis(), fetchHistory()]);
      setLoading(false);
    };
    
    if (user && token) {
      loadData();
    }
  }, [user, token]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'bg-green-100 text-green-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'good': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'critical': return <XCircle className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-blue-100 text-blue-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-lg">Chargement des données SpinTouch...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Beaker className="w-8 h-8 mr-3 text-blue-600" />
            SpinTouch LaMotte
          </h1>
          <p className="text-gray-600 mt-2">Analyse intelligente de votre eau de piscine</p>
        </div>
        <Button 
          onClick={simulateSpinTouchData} 
          disabled={simulating}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {simulating ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Simulation...
            </>
          ) : (
            <>
              <Activity className="w-4 h-4 mr-2" />
              Simuler Test
            </>
          )}
        </Button>
      </div>

      {/* Latest Analysis */}
      {latestAnalysis ? (
        <div className="mb-8">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                  Dernière Analyse Expert
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <Badge className={getPriorityColor(latestAnalysis.priority_level)}>
                    {latestAnalysis.priority_level?.toUpperCase()}
                  </Badge>
                  <Badge className={getStatusColor(latestAnalysis.expert_analysis?.status)}>
                    {getStatusIcon(latestAnalysis.expert_analysis?.status)}
                    <span className="ml-1">{latestAnalysis.expert_analysis?.status?.toUpperCase()}</span>
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <Alert className="mb-6">
                <Activity className="w-4 h-4" />
                <AlertDescription className="text-lg font-medium">
                  {latestAnalysis.expert_analysis?.summary || "Analyse en cours..."}
                </AlertDescription>
              </Alert>

              {/* Measurements Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {latestAnalysis.raw_data?.measurements && Object.entries(latestAnalysis.raw_data.measurements).map(([param, value]) => {
                  const paramInfo = latestAnalysis.expert_analysis?.parameters?.[param];
                  return (
                    <Card key={param} className="border-l-4 border-l-blue-500">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-600 capitalize">
                            {param === 'ph' ? 'pH' : param.replace('_', ' ')}
                          </span>
                          {paramInfo && (
                            <Badge className={getStatusColor(paramInfo.status)} variant="outline">
                              {getStatusIcon(paramInfo.status)}
                            </Badge>
                          )}
                        </div>
                        <div className="text-2xl font-bold text-gray-900">
                          {value} {param !== 'ph' ? 'ppm' : ''}
                        </div>
                        {paramInfo?.comment && (
                          <p className="text-xs text-gray-500 mt-1">{paramInfo.comment}</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Environmental Data */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <Thermometer className="w-5 h-5 text-orange-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Température</p>
                    <p className="font-semibold">{latestAnalysis.raw_data?.temperature}°C</p>
                  </div>
                </div>
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <MapPin className="w-5 h-5 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Localisation</p>
                    <p className="font-semibold">{latestAnalysis.raw_data?.location || 'Non spécifiée'}</p>
                  </div>
                </div>
                <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Analysé le</p>
                    <p className="font-semibold">
                      {new Date(latestAnalysis.created_at).toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Detailed Analysis */}
              {latestAnalysis.expert_analysis?.detailed_analysis && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg">Analyse Détaillée</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 leading-relaxed">
                      {latestAnalysis.expert_analysis.detailed_analysis}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Immediate Actions */}
              {latestAnalysis.expert_analysis?.immediate_actions?.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="text-lg text-orange-600">Actions Immédiates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {latestAnalysis.expert_analysis.immediate_actions.map((action, index) => (
                        <div key={index} className="flex items-start p-3 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                          <AlertTriangle className="w-5 h-5 text-orange-500 mr-3 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-orange-800">{action.action}</p>
                            <div className="flex items-center mt-1 space-x-2">
                              <Badge className={getPriorityColor(action.priority)} variant="outline">
                                {action.priority}
                              </Badge>
                              <span className="text-sm text-orange-600">⏱️ {action.timeframe}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Product Recommendations */}
              {latestAnalysis.expert_analysis?.product_recommendations?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg text-blue-600">Produits Recommandés</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {latestAnalysis.expert_analysis.product_recommendations.map((product, index) => (
                        <div key={index} className="p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{product.product_name}</h4>
                              <p className="text-blue-600 font-medium mt-1">{product.dosage}</p>
                              <p className="text-gray-600 mt-2">{product.reason}</p>
                              {product.usage && (
                                <p className="text-sm text-gray-500 mt-2">
                                  <strong>Usage:</strong> {product.usage}
                                </p>
                              )}
                            </div>
                            <Button variant="outline" size="sm" className="ml-4">
                              <Droplets className="w-4 h-4 mr-2" />
                              Ajouter au panier
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="mb-8">
          <CardContent className="text-center py-12">
            <Beaker className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune analyse SpinTouch</h3>
            <p className="text-gray-600 mb-4">
              Connectez votre testeur SpinTouch LaMotte pour recevoir des analyses intelligentes de votre eau.
            </p>
            <Button onClick={simulateSpinTouchData} disabled={simulating}>
              {simulating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Simulation en cours...
                </>
              ) : (
                <>
                  <Activity className="w-4 h-4 mr-2" />
                  Simuler un test
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Historique des Analyses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {history.slice(0, 5).map((analysis) => (
                <div key={analysis.analysis_id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${getStatusColor(analysis.expert_analysis?.status)}`}>
                      {getStatusIcon(analysis.expert_analysis?.status)}
                    </div>
                    <div>
                      <p className="font-medium">
                        {new Date(analysis.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      <p className="text-sm text-gray-600">
                        {analysis.expert_analysis?.summary || 'Analyse disponible'}
                      </p>
                    </div>
                  </div>
                  <Badge className={getPriorityColor(analysis.priority_level)}>
                    {analysis.priority_level}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SpinTouchPage;